import { state, $, isEmail } from "./state.js";
import { hasDB, sbInsert, sbFunction, sbRpc } from "./supabase.js";
import { totalDue, syncCart, renderDiscount, closeCart } from "./render.js";
import { saveCart } from "./cart.js";
import { openDownloadModal } from "./downloads.js";

/** Human-readable order reference, e.g. CTA-M4K2P9. Also used as the Paystack transaction reference. */
function makeReference() {
  return "CTA-" + Date.now().toString(36).toUpperCase().slice(-6);
}

/**
 * Paystack's InlineJS, loaded on first use (when the buyer reaches the
 * details step — see main.js) rather than on every page view. A script tag,
 * not an npm package: Paystack only ships it from their own CDN. Resolves to
 * the PaystackPop class; a failed load resets so the next try starts fresh.
 */
let paystackScript = null;
export function loadPaystack() {
  if (window.PaystackPop) return Promise.resolve(window.PaystackPop);
  paystackScript ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.paystack.co/v2/inline.js";
    s.async = true;
    s.onload = () => (window.PaystackPop ? resolve(window.PaystackPop) : reject(new Error("PaystackPop missing")));
    s.onerror = () => {
      s.remove();
      paystackScript = null;
      reject(new Error("Paystack script failed to load"));
    };
    document.head.append(s);
  });
  return paystackScript;
}

/**
 * The popup said the payment went through. That's the browser's word, so it
 * isn't what marks the order paid (paystack-webhook does) — it just empties
 * the cart and opens the download modal, which checks the real status and
 * quietly retries while the webhook catches up.
 */
function paymentDone(reference, email) {
  state.cart = [];
  state.discount = null;
  state.checkoutStep = "cart";
  saveCart();
  syncCart();
  closeCart();
  openDownloadModal({ reference, email, auto: true });
}

/**
 * Save the order, then hand off to Paystack.
 *
 * The order is written with status "pending" first, then paystack-initiate
 * looks it up and starts a Paystack transaction, which opens in Paystack's
 * popup on this page (falling back to their hosted page if the popup script
 * can't load). Nothing here marks the order paid — that
 * only happens when Paystack calls the paystack-webhook Edge Function,
 * which is the only thing allowed to flip the status (see its comment for
 * why: the browser is never trusted with that).
 */
export async function placeOrder() {
  const nameInput = $("#buyerName");
  const emailInput = $("#buyerEmail");
  const termsInput = $("#acceptTerms");
  const errorBox = $("#checkoutError");
  const button = $("#placeOrder");

  const full_name = (nameInput?.value || "").trim();
  const email = (emailInput?.value || "").trim();
  errorBox.innerHTML = "";

  if (!isEmail(email)) {
    errorBox.innerHTML = `<div class="err">Enter a valid email address so your notes can reach you.</div>`;
    emailInput?.focus();
    return;
  }

  if (!termsInput?.checked) {
    errorBox.innerHTML = `<div class="err">Confirm you understand the purchase is final before placing your order.</div>`;
    termsInput?.focus();
    return;
  }

  button.textContent = "Saving order…";
  button.disabled = true;

  const reference = makeReference();
  const order = {
    reference,
    email,
    full_name: full_name || null,
    // Snapshot of the cart, so the order stays correct if a product is later edited.
    items: state.cart.map((i) => ({
      product_id: i.id,
      code: i.code,
      title: i.title,
      price_cents: i.price_cents,
    })),
    // Not trusted — price_order() in supabase/schema.sql overwrites this on
    // insert, from the products table and a server-side check of the code.
    total_cents: totalDue(),
    discount_code: state.discount?.code || null,
    status: "pending",
    // Record-keeping, not just a UI gate — see the terms_accepted_at column
    // comment in supabase/schema.sql. Only reachable once termsInput.checked
    // is confirmed true above.
    terms_accepted_at: new Date().toISOString(),
  };

  try {
    if (hasDB) {
      await sbInsert("orders", order);

      // So the download modal can prefill and auto-check on return from
      // Paystack (see main.js's ?paid=1 handling) without asking the buyer
      // to retype the email they just gave us. Stays on their device only —
      // same as the cart (see privacy.html).
      try {
        localStorage.setItem("cta_last_email", email);
        localStorage.setItem("cta_last_ref", reference);
      } catch {
        // ignore — worst case the download modal just isn't prefilled
      }

      button.textContent = "Opening payment…";
      const [{ authorization_url, access_code }, PaystackPop] = await Promise.all([
        sbFunction("paystack-initiate", { reference }),
        loadPaystack().catch(() => null),
      ]);

      if (access_code && PaystackPop) {
        // Paystack's own popup, over this page — the buyer never leaves the site.
        new PaystackPop().resumeTransaction(access_code, {
          onSuccess: () => paymentDone(reference, email),
          onCancel: () => {
            // The pending order just stays pending; trying again writes a new one.
            errorBox.innerHTML = `<p class="note" style="text-align:left;">Payment cancelled. Your cart is still here when you're ready.</p>`;
            button.textContent = "Place order";
            button.disabled = false;
          },
          onError: () => {
            errorBox.innerHTML = `<div class="err">Payment couldn't be opened just now. Try again.</div>`;
            button.textContent = "Place order";
            button.disabled = false;
          },
        });
        return;
      }

      // Fallback: Paystack's script didn't load (blocked or offline), or an
      // older paystack-initiate that only returns the URL — send the buyer to
      // the hosted page instead. Cart cleared now, since they're leaving; it's
      // only cleared once Paystack has given us a page, so a failed
      // paystack-initiate above still leaves the cart there to retry with.
      button.textContent = "Redirecting to payment…";
      state.cart = [];
      state.discount = null;
      saveCart();
      window.location.href = authorization_url; // main.js's ?paid=1 handling picks up from here
      return;
    }

    // No Supabase configured — nothing to charge, just fake the confirmation
    // so the shop stays clickable for design work (see CLAUDE.md).
    state.lastOrderRef = `Order ${reference} (demo — Supabase not connected)`;
    state.cart = [];
    state.discount = null;
    saveCart();
    state.checkoutStep = "done";
    syncCart();
  } catch (err) {
    console.error(err);
    // price_order()'s own messages (see supabase/schema.sql) come back in
    // the insert's error body — the two a buyer can actually act on.
    if (/Invalid discount code/.test(err.message)) {
      state.discount = null;
      renderDiscount();
      errorBox.innerHTML = `<div class="err">That discount code is no longer valid, so it's been removed and your total updated. Place the order again to continue.</div>`;
    } else if (/no longer available/.test(err.message)) {
      errorBox.innerHTML = `<div class="err">One of the notes in your cart is no longer available. Remove it and try again.</div>`;
    } else {
      errorBox.innerHTML = `<div class="err">Couldn't start payment just now. Check your connection and try again.</div>`;
    }
    button.textContent = "Place order";
    button.disabled = false;
  }
}

/**
 * Check a code against the database and, if it's real, show the discount.
 * This is only ever for display — the order insert re-checks the code
 * server-side (price_order()), so nothing here decides what's charged.
 */
export async function applyDiscount() {
  const input = $("#discountInput");
  const errorBox = $("#discountError");
  const button = $("#applyDiscount");
  const code = (input?.value || "").trim().toUpperCase();
  errorBox.innerHTML = "";
  if (!code) return input?.focus();

  if (!hasDB) {
    errorBox.innerHTML = `<div class="err">Discount codes only work on the live shop.</div>`;
    return;
  }

  button.disabled = true;
  button.textContent = "Checking…";
  try {
    const percent = await sbRpc("discount_percent", { p_code: code });
    if (!percent) {
      errorBox.innerHTML = `<div class="err">That code isn't valid.</div>`;
      input.focus();
      return;
    }
    state.discount = { code, percent };
    renderDiscount();
  } catch (err) {
    console.error(err);
    errorBox.innerHTML = `<div class="err">Couldn't check that code just now. Try again.</div>`;
  } finally {
    // Gone from the page after a successful apply (renderDiscount swaps
    // the field for the "applied" line), so only reset it if it's still there.
    if (button.isConnected) {
      button.disabled = false;
      button.textContent = "Apply";
    }
  }
}

export function removeDiscount() {
  state.discount = null;
  renderDiscount();
}
